<template>
  <div class="item_container">
    <div class="item_title" v-text="$t(automationEffectGroup.effectLabel)" />
    <!-- EFFECT ITEM -->
    <div class="items">
      <template v-if="automationEffectGroup.items.length < 5">
        <AutomationEffectItem
          v-for="(automationEffect, index) in automationEffectGroup.items"
          :key="index"
          :automation-effect="automationEffect"
          :has-enoxa="hasEnoxa"
          :status-id="statusId"
        />
      </template>
      <template v-else>
        <div class="effect_items_container">
          <!-- ITEM SECTION -->
          <div class="effect_items">
            <div class="left_section">
              <v-autocomplete
                v-model="activeEffectId"
                :items="automationEffectGroup.items"
                outlined
                dense
                hide-details
                item-text="effectExtraLabel"
                item-value="id"
              />
            </div>
            <div class="right_section">
              <div class="action_section">
                <button
                  v-if="!doingAction"
                  :disabled="!!errorMessage || !activeEffect"
                  class="action_btn"
                  :class="{
                    custom_tooltip: !!errorMessage,
                  }"
                  @click="runEffect(activeEffect)"
                >
                  <div v-if="errorMessage" class="tooltip_content">
                    {{ errorMessage }}
                  </div>
                  <BaseIcon class="btn_icon" icon="$mdiPlay" />
                </button>
                <v-progress-circular
                  v-else
                  :size="22"
                  class="action_progress"
                  indeterminate
                  color="primary"
                />
              </div>
            </div>
          </div>
          <!-- EFFECT LOGS -->
          <div v-if="recentLogs.length" class="logs_section">
            <template v-if="!displayOlderLogs">
              <div
                v-if="olderLogs.length"
                class="older_log_count"
                @click="displayOlderLogs = true"
              >
                {{ `+${olderLogs.length}` }}
              </div>
            </template>
            <template v-else>
              <div
                v-for="log in olderLogs"
                :key="log.id"
                class="log_item custom_tooltip"
              >
                <BaseIcon icon="$mdiCheck" color="white" small />

                <div class="tooltip_content">
                  <div class="author">
                    {{
                      log.author
                        ? $t("automation.started_by", { name: log.author })
                        : $t("automation.send_automatically")
                    }}
                  </div>

                  <div class="date_created">
                    {{
                      `${$t("utils.on")} ${$d(
                        new Date(log.dateCreated),
                        "veryshort"
                      )}`
                    }}
                  </div>
                </div>
              </div>
            </template>
            <div
              v-for="log in recentLogs"
              :key="log.id"
              class="log_item custom_tooltip"
            >
              <BaseIcon icon="$mdiCheck" color="white" small />

              <div class="tooltip_content">
                <div class="author">
                  {{
                    log.author
                      ? $t("automation.started_by", { name: log.author })
                      : $t("automation.send_automatically")
                  }}
                </div>

                <div class="date_created">
                  {{
                    `${$t("utils.on")} ${$d(
                      new Date(log.dateCreated),
                      "veryshort"
                    )}`
                  }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { AutomationEffectGroup } from "@/core/Models/AutomationEffect";
import AutomationEffectItem from "./AutomationEffectItem.vue";
import { AUTOMATION_EFFECT } from "@/core/Utils/types.utils";
import FolderAPI from "@/modules/Folder/Services/folder.api";
export default {
  name: "FolderAutomationEffectGroup",
  components: {
    AutomationEffectItem,
  },
  props: {
    automationEffectGroup: {
      type: AutomationEffectGroup,
      required: true,
    },
    hasEnoxa: {
      type: Boolean,
    },
    statusId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      doingAction: false,
      activeEffectId: null,
      displayOlderLogs: false,
      recentLogCount: 5,
    };
  },
  computed: {
    activeEffect() {
      if (!this.activeEffectId) return null;
      return this.automationEffectGroup.items.find(
        (effect) => effect.id === this.activeEffectId
      );
    },
    recentLogs() {
      return this.activeEffect.logs
        .slice()
        .reverse()
        .slice(-this.recentLogCount);
    },
    olderLogs() {
      return this.activeEffect.logs
        .slice()
        .reverse()
        .slice(0, -this.recentLogCount);
    },
    errorMessage() {
      if (!this.activeEffect) return "";
      switch (this.activeEffect.action) {
        case AUTOMATION_EFFECT.SEND_FORM:
        case AUTOMATION_EFFECT.SEND_OFFER:
        case AUTOMATION_EFFECT.SEND_CAMPAIGN:
          if (this.activeFolder?.client?.isTemporary)
            return this.$t("utils.require_assign_contact");
          break;
        case AUTOMATION_EFFECT.SEND_TO_GED:
          if (!this.hasEnoxa) return this.$t("utils.enoxa_required");
          break;
      }
      if (this.activeEffect.unique && this.activeEffect.logs.length != 0)
        return this.$t("automation.dialog.one_time_effect");
      return "";
    },
  },
  created() {
    this.setDefaultActiveEffect();
  },
  methods: {
    setDefaultActiveEffect() {
      if (this.automationEffectGroup.items)
        this.activeEffectId = this.automationEffectGroup.items[0].id;
    },
    async runEffect(automationEffect) {
      if (this.errorMessage || !automationEffect) return;
      this.doingAction = true;
      try {
        await FolderAPI.postFolderEffect({
          organizationId: this.$route.params.organizationId,
          folderId: this.$route.params.folderId,
          effectId: automationEffect.id,
        });
        switch (automationEffect.action) {
          case AUTOMATION_EFFECT.SEND_FORM:
          case AUTOMATION_EFFECT.SEND_OFFER:
          case AUTOMATION_EFFECT.SEND_CAMPAIGN:
            await this.$store.dispatch(
              "folder/getOrganizationOffersRecordFolder",
              {
                organizationId: this.$route.params.organizationId,
                folderId: this.$route.params.folderId,
                page: 1,
              }
            );
            break;
          case AUTOMATION_EFFECT.ARCHIVE_FOLDER:
          case AUTOMATION_EFFECT.ASSIGN_COLLABORATOR:
          case AUTOMATION_EFFECT.SET_STATUS:
            await this.$store.dispatch("folder/fetchActiveFolder", {
              organizationId: this.$route.params.organizationId,
              folderId: this.$route.params.folderId,
            });
            await this.$store.dispatch("folder/fetchClientOwnfolder", {
              organizationId: this.$route.params.organizationId,
              folderId: this.$route.params.folderId,
            });
            break;
          case AUTOMATION_EFFECT.GENERATE_DOC:
            if (this.activeFolder.workflow) {
              this.$store.commit("workflow/SET_ACTIVE_WORKFLOW", null);
              await this.$store.dispatch("workflow/fetchActiveWorkflow", {
                organizationId: this.$route.params.organizationId,
                workflowId: this.activeFolder.workflow,
              });
            }
            break;
        }
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.action_successfully_completed"),
          type: "SUCCESS",
        });
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.$store.dispatch("workflow/fetchFolderStatusEffects", {
        organizationId: this.$route.params.organizationId,
        folderId: this.$route.params.folderId,
        statusId: this.statusId,
      });
      this.doingAction = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.item_container {
  width: 100%;
  background: #fff;
  padding: 24px;
  margin-top: 24px;
  border-radius: 5px;
  box-shadow: 0px 2px 2px rgba(0, 15, 144, 0.2),
    0px 5px 15px rgba(20, 20, 243, 0.1);
  .item_title {
    color: #353542;
    font-family: Inter;
    font-size: 14px;
    font-weight: 600;
    line-height: 22px;
    margin-bottom: 8px;
  }
  .items {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .effect_items_container {
    .effect_items {
      display: flex;
      align-items: center;
      gap: 12px;
      .left_section {
        flex: 1;
      }
      .right_section {
        flex: none;
        width: 22px;
        height: 22px;
        margin-left: auto;
        .action_btn {
          width: 22px;
          height: 22px;
          border: 2px solid #707080;
          background: #fff;
          display: flex;
          align-items: center;
          border-radius: 50%;
          justify-content: center;
          flex: none;
          cursor: pointer;
          .btn_icon {
            ::v-deep {
              .v-icon__svg {
                width: 14px;
                height: 14px;
                color: #707080;
                flex: none;
              }
            }
          }
          &:disabled {
            border-color: #d9d9d9;
            .btn_icon {
              ::v-deep {
                .v-icon__svg {
                  color: #d9d9d9;
                }
              }
            }
          }
        }
        .custom_tooltip {
          position: relative;
          .tooltip_content {
            position: absolute;
            top: 0;
            right: 0;
            transform: translate(22px, calc(-100% - 20px));
            padding: 12px 16px;
            border-radius: 5px;
            pointer-events: none;
            opacity: 0;
            transition: opacity 0.25s;
            background-color: #ff5267;
            z-index: 10;
            width: 200px;
            font-size: 14px;
            line-height: 18px;
            color: #fff;
            text-align: center;
          }
          &::before {
            content: "";
            position: absolute;
            top: 0;
            right: 0;
            transform: translate(2px, -21px);
            height: 0;
            width: 0;
            border: 12px solid transparent;
            border-top-color: #ff5267;
            z-index: 11;
            pointer-events: none;
            opacity: 0;
            transition: opacity 0.25s;
          }

          &:hover {
            .tooltip_content {
              opacity: 1;
            }
            &::before {
              opacity: 1;
            }
          }
        }
        .action_progress {
          width: 22px;
          height: 22px;
          flex: none;
        }
      }
    }
    .logs_section {
      margin-top: 12px;
      display: flex;
      gap: 5px;
      align-items: center;
      width: 100%;
      flex-wrap: wrap;
      .older_log_count {
        color: #707080;
        font-family: Inter;
        font-size: 12px;
        font-style: normal;
        font-weight: 600;
        line-height: 18px;
        cursor: pointer;
        text-decoration: underline;
        color: #2536cc;
      }
      .log_item {
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #18a300;
        border-radius: 50%;
      }
      .custom_tooltip {
        position: relative;
        .tooltip_content {
          position: absolute;
          top: 0;
          right: 0;
          transform: translate(110px, calc(-100% - 20px));
          padding: 12px 16px;
          border-radius: 5px;
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.25s;
          background-color: #707080;
          z-index: 10;
          font-size: 14px;
          line-height: 18px;
          color: #fff;
          text-align: start;
          .date_created {
            color: #b2b2b7;
          }
          .author {
            white-space: nowrap;
          }
        }
        &::before {
          content: "";
          position: absolute;
          top: 0;
          right: 0;
          transform: translate(2px, -21px);
          height: 0;
          width: 0;
          border: 12px solid transparent;
          border-top-color: #707080;
          z-index: 11;
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.25s;
        }

        &:hover {
          .tooltip_content {
            opacity: 1;
          }
          &::before {
            opacity: 1;
          }
        }
      }
    }
  }
}
</style>
